import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertCircle, Loader2, Upload, X } from "lucide-react";

const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif)(?:$|[?#])/i;
const YT_RE = /(youtube\.com|youtu\.be|youtube-nocookie\.com)/i;

/** Return {ok: true} or {ok:false, error:"..."} for a URL entered by the admin. */
function validateImageUrl(raw) {
  const url = (raw || "").trim();
  if (!url) return { ok: false, error: "Please enter an image URL." };
  if (url.startsWith("data:image/")) return { ok: true };
  if (YT_RE.test(url)) {
    return { ok: false, error: "Please enter a direct image URL, not a YouTube video link." };
  }
  if (!/^https?:\/\//i.test(url)) {
    return { ok: false, error: "URL must start with http:// or https://" };
  }
  if (!IMAGE_EXT_RE.test(url)) {
    return {
      ok: false,
      error: "URL must end in .jpg, .jpeg, .png, .webp or .gif",
    };
  }
  return { ok: true };
}

/** Preload the image; resolve true iff it loads within `timeoutMs`. */
function preloadImage(url, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const img = new Image();
    let done = false;
    const finish = (ok) => {
      if (done) return;
      done = true;
      resolve(ok);
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
    setTimeout(() => finish(false), timeoutMs);
  });
}

/**
 * CourseThumbnailUploader
 *  - Upload flow: POST /admin/upload/thumbnail (multipart; auto Content-Type).
 *  - URL flow: two-step — validate + preload — only commit on success.
 *  - Never clears the existing thumbnail on invalid input or load failure.
 *  - Optional `onImmediateSave(url)` persists the change server-side.
 */
export default function CourseThumbnailUploader({ value, onChange, onImmediateSave }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [tempUrl, setTempUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [applying, setApplying] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 3 * 1024 * 1024) return toast.error("Max 3 MB");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload/thumbnail", fd);
      onChange(data.url);
      toast.success("Thumbnail uploaded");
      if (onImmediateSave) {
        try { await onImmediateSave(data.url); }
        catch (e) { toast.error("Uploaded, but auto-save failed: " + (formatApiError(e.response?.data?.detail) || e.message)); }
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(status ? `Upload failed (${status}): ${detail}` : `Upload failed: ${detail}`);
    } finally {
      setUploading(false);
    }
  }

  function openUrlMode() {
    setUrlMode(true);
    setTempUrl("");
    setUrlError("");
  }

  function cancelUrl() {
    setUrlMode(false);
    setTempUrl("");
    setUrlError("");
  }

  async function applyUrl() {
    setUrlError("");
    const v = validateImageUrl(tempUrl);
    if (!v.ok) {
      setUrlError(v.error);
      return; // do NOT change the existing thumbnail
    }
    setApplying(true);
    const url = tempUrl.trim();
    try {
      const loaded = await preloadImage(url);
      if (!loaded) {
        setUrlError("The image could not be loaded from that URL. Please check the link and try again.");
        return; // preserve existing thumbnail
      }
      onChange(url);
      if (onImmediateSave) {
        try {
          await onImmediateSave(url);
          toast.success("Thumbnail updated");
        } catch (e) {
          toast.error("Loaded, but save failed: " + (formatApiError(e.response?.data?.detail) || e.message));
          return;
        }
      } else {
        toast.success("Thumbnail set");
      }
      setUrlMode(false);
      setTempUrl("");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div>
      <p className="text-sm font-medium mb-2">Course thumbnail</p>
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="h-32 w-52 rounded-2xl bg-secondary border border-border overflow-hidden relative flex-shrink-0">
          {value ? (
            <>
              <img src={value} alt="thumb" className="w-full h-full object-cover" data-testid="thumbnail-preview" />
              <button onClick={() => onChange("")}
                data-testid="remove-thumbnail-btn"
                className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
          )}
        </div>

        <div className="space-y-2 flex-1 w-full">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
            data-testid="thumbnail-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}
              data-testid="upload-thumbnail-btn" disabled={uploading} className="rounded-full">
              <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading..." : "Upload image"}
            </Button>
            {!urlMode && (
              <Button type="button" variant="ghost" onClick={openUrlMode}
                data-testid="open-url-mode-btn" className="rounded-full">
                Or paste image URL
              </Button>
            )}
          </div>

          {urlMode && (
            <div className="space-y-2 rounded-2xl border border-border p-3">
              <Input
                value={tempUrl}
                onChange={(e) => { setTempUrl(e.target.value); if (urlError) setUrlError(""); }}
                placeholder="https://example.com/image.jpg"
                data-testid="thumbnail-url-input"
                aria-invalid={!!urlError}
                className={`rounded-xl h-10 ${urlError ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyUrl(); } }}
                autoFocus
              />
              {urlError && (
                <div data-testid="thumbnail-url-error"
                     className="flex items-start gap-2 text-xs text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{urlError}</span>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Direct image link ending in <code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, <code>.webp</code> or <code>.gif</code>.
                YouTube links are not accepted.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <Button type="button" onClick={applyUrl} disabled={applying}
                  data-testid="apply-url-btn"
                  className="rounded-full royal-gradient border-0 text-white btn-lift">
                  {applying ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Checking…</>) : "Apply URL"}
                </Button>
                <Button type="button" variant="ghost" onClick={cancelUrl}
                  data-testid="cancel-url-btn" className="rounded-full">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">JPG, PNG, WEBP or GIF · max 3 MB (upload) · any HTTPS image URL (paste).</p>
        </div>
      </div>
    </div>
  );
}
